import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Layout from "../../components/layout/layout";
import Button from "../../components/library/button";
import {
  ChartLineLightIcon,
  ExploreIcon,
  LightbulbIcon,
  MedalLightIcon,
  SwordIcon,
  UsersTreeLightIcon,
} from "../../assets/images/svgExports";

import promo1 from "../../assets/images/success-story-promo1.png";
import Testimony from "../../assets/images/success-story-testimonial.png";

type StatProps = {
  percentage: string;
  title: string;
  description?: string;
  icon: React.ComponentType<{ color?: string }>;
};

type PromoCardDetailsProps = {
  title: string;
  description: string;
  icon: React.ComponentType<{ color?: string }>;
};

type Testimonial = {
  name: string;
  title: string;
  company: string;
  quote: string;
  imageSrc: string;
};

type SuccessStory = {
  id: string;
  title: string;
  promoImage: string;
  promoCardDetails: PromoCardDetailsProps[];
  stats: StatProps[];
  testimonials: Testimonial[];
  industry: string;
};

const successStories: SuccessStory[] = [
  {
    id: "mtn-mega-billion",
    title: "MTN Mega Billion Promo",
    promoImage: promo1,
    industry: "Telecommunications",
    promoCardDetails: [
      {
        title: "The Challenge",
        description:
          "MTN needed to run a nationwide promo handling millions of users with fair draws and instant rewards.",
        icon: SwordIcon,
      },
      {
        title: "Our Solution",
        description:
          "Bridgetune powered the digital backbone with USSD, SMS, and web access for seamless participation.",
        icon: LightbulbIcon,
      },
    ],
    stats: [
      {
        percentage: "+145%",
        title: "Subscriber Engagement",
        description: "Increase in active participation",
        icon: UsersTreeLightIcon,
      },
      {
        percentage: "+32%",
        title: "Revenue Uplift",
        description: "Growth in campaign period",
        icon: ChartLineLightIcon,
      },
      {
        percentage: "-18%",
        title: "Churn Reduction",
        description: "Among campaign participants",
        icon: MedalLightIcon,
      },
    ],
    testimonials: [
      {
        name: "Funke Opeke",
        title: "Campaign Director",
        company: "MTN Nigeria",
        quote:
          "Bridgetune powered the digital backbone with USSD, SMS, and web access for seamless participation.",
        imageSrc: Testimony,
      },
      {
        name: "Funke Opeke",
        title: "Campaign Director",
        company: "MTN Nigeria",
        quote:
          "Bridgetune powered the digital backbone with USSD, SMS, and web access for seamless participation.",
        imageSrc: Testimony,
      },
      {
        name: "Funke Opeke",
        title: "Campaign Director",
        company: "MTN Nigeria",
        quote:
          "Bridgetune powered the digital backbone with USSD, SMS, and web access for seamless participation.",
        imageSrc: Testimony,
      },
    ],
  },
];

const PromoSection: React.FC<{ story: SuccessStory }> = ({ story }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      gsap.from(".promo-card", {
        opacity: 0,
        y: 40,
        stagger: 0.2,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 80%",
        },
      });

      gsap.from(".stat-card", {
        opacity: 0,
        scale: 0.8,
        stagger: 0.15,
        duration: 0.7,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 80%",
        },
      });
    }, ref);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} className="promo-section">
      <div className="flex flex-col gap-6 lg:flex-row items-center">
        <div className="flex-1 h-[650px]">
          <img
            src={story.promoImage}
            alt={story.title}
            className="w-full h-full object-contain rounded-[32px]"
          />
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="inline-flex items-center bg-[#2c73db33] text-[.6rem] font-medium py-2 px-3 rounded-full mb-3 border border-[#2C73DB] shadow-[0_0_18px_rgba(59,130,246,0.25)] text-[#1e1e1e]">
            {story.industry}
          </div>
          <h2 className="mb-6 text-2xl md:text-3xl text-black">
            {story.title}
          </h2>

          <div className="flex flex-col gap-4 mb-8">
            {story.promoCardDetails.map((card, i) => (
              <div
                key={i}
                className="promo-card bg-[#F0F6FF] p-4 px-4 md:px-6 rounded-3xl flex items-center"
              >
                <card.icon />
                <div className="ml-4">
                  <h3 className="text-md font-bold text-gray-700">
                    {card.title}
                  </h3>
                  <p className="text-xs text-[#8e8e8e]">{card.description}</p>
                </div>
              </div>
            ))}
          </div>

          <h6 className="text-black text-xl mb-3">The Impact</h6>
          <div className="flex flex-col gap-4">
            {story.stats.map((stat, i) => (
              <div
                key={i}
                className="stat-card bg-[#2C73DB] px-4 md:px-6  py-4 rounded-3xl flex items-center"
              >
                <stat.icon color="white" />
                <div className="ml-4">
                  <h3 className="font-bold text-white">
                    {stat.percentage}{" "}
                    <span className="text-xs ml-2 font-[100]">
                      {stat.title}
                    </span>
                  </h3>
                  <p className="text-xs text-white">{stat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhatTheyreSaying: React.FC<{ testimonials: Testimonial[] }> = ({
  testimonials,
}) => {
  return (
    <section className="testimonial-section">
      <h2 className="text-xl md:text-3xl text-center mb-6 text-slate-900">
        What They’re Saying
      </h2>

      <div className="flex gap-8 overflow-x-auto lg:px-6">
        {testimonials.map((t, i) => (
          <div
            className={`flex w-full bg-sky-50 flex-col items-start p-6 rounded-3xl shadow-sm min-w-[400px] md:max-w-[200px] lg:max-w-[200px]`}
          >
            <div className="flex items-center gap-4">
              <img
                src={t.imageSrc}
                alt={t.name}
                className="w-20 h-20 rounded-full object-cover mb-4"
              />
              <div className="text-center w-fit flex flex-col items-start">
                <h3 className="text-sm font-interstate md:text-xl font-medium text-slate-600 ">
                  {t.name}
                </h3>
                <div className="text-xs md:text-md flex whitespace-nowrap md:whitespace-normal text-[#2C73DB] items-center gap-2">
                  <p className="text-xs md:text-sm font-interstate ">
                    {t.title}
                  </p>
                  <p className="text-xs md:text-sm ">{t.company}</p>
                </div>
              </div>
            </div>
            <p className="mt-4 max-w-[330px] md:max-w-[400px] text-xs md:text-sm text-[#8e8e8e]">
              {t.quote}
            </p>
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-8">
        <Button className="bg-[#2C73DB] px-12 gap-2">
          View Full Case Study
          <ExploreIcon />
        </Button>
      </div>
    </section>
  );
};

const SuccessStoriesPage: React.FC = () => {
  return (
    <Layout
      footerData={{
        headerText: "Ready to Create Your Success Story?",
        bodyText:
          "Join hundreds of African brands using Bridgetune to transform customer engagement.",
      }}
    >
      <div className="pb-32">
        <section
          className="
            relative bg-[#2C73DB] mb-20
            rounded-bl-[80px] rounded-br-[80px]
            md:rounded-bl-[160px] md:rounded-br-[160px]
            lg:rounded-bl-[200px] lg:rounded-br-[200px]
            text-white pt-28 flex items-end justify-center
          "
        >
          <div className="max-w-5xl relative px-4 sm:px-6">
            <div className="loyalty-hero-heading text-center mb-[10%]">
              <div className="inline-flex items-center bg-[#ffffff33] text-xs font-[300] py-2 px-3 rounded-full mb-3 border border-[#fff] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                Success Stories{" "}
              </div>

              <h1 className="text-3xl lg:text-4xl font-medium leading-tight mb-4 px-2">
                Real Results from Real Businesses
              </h1>
              <p className="text-sm md:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                See how leading African brands use Bridgetune to transform
                customer engagement, drive loyalty, and accelerate growth across
                the continent.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto space-y-32 px-6">
          {successStories.map((story) => (
            <div key={story.id} className="space-y-10">
              <PromoSection story={story} />
              <WhatTheyreSaying testimonials={story.testimonials} />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default SuccessStoriesPage;
